import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import {
  HiCheck,
  HiExternalLink,
  HiExclamationCircle,
  HiChevronDown,
} from "react-icons/hi";
import { SecondaryButton } from "../styledComponents";
import { IClipForDeletion, IStorageStatus } from "../@types";
import { formatNumber } from "MVVM/Utilities/Clip";

type SortOption = "LATEST" | "VIEWS" | "BEST";

interface DeleteBrowseProps {
  clips: IClipForDeletion[];
  isLoading: boolean;
  selectedClipIds: string[];
  onSelectClip: (clipId: string) => void;
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
  onDelete: () => void;
  onBack: () => void;
  isDeleting: boolean;
  backButtonText?: string;
  hideSortOptions?: boolean;
  onOpenClip?: (clipId: string) => void;
  actionLabel?: string;
  storageStatus?: IStorageStatus;
  onUpgrade?: () => void;
  isUnlimited?: boolean;
}

export const DeleteBrowse = ({
  clips,
  isLoading,
  selectedClipIds,
  onSelectClip,
  onSortChange,
  currentSort,
  onDelete,
  onBack,
  isDeleting,
  backButtonText = "Close",
  hideSortOptions = false,
  onOpenClip,
  actionLabel = "Unsave",
  storageStatus,
  onUpgrade,
  isUnlimited = false,
}: DeleteBrowseProps) => {
  const handleOpenClip = (e: React.MouseEvent, clipId: string) => {
    e.stopPropagation();
    onOpenClip?.(clipId);
  };

  const showStorageBar =
    storageStatus && !isUnlimited && storageStatus.limit > 0;
  const percentage = showStorageBar
    ? (storageStatus.savedCount / storageStatus.limit) * 100
    : 0;

  return (
    <>
      {showStorageBar && (
        <StorageSection p="20px 28px">
          <StorageRow mb="10px">
            <StorageCount>
              {storageStatus.savedCount} / {storageStatus.limit} clips stored
            </StorageCount>
            {onUpgrade && (
              <UpgradeLink onClick={onUpgrade}>Upgrade →</UpgradeLink>
            )}
          </StorageRow>
          <ProgressBar>
            <ProgressFill $percentage={percentage} />
          </ProgressBar>
        </StorageSection>
      )}

      <Box p="16px 28px 24px">
        {!hideSortOptions && (
          <SortRow mb="12px">
            <SortLabel>Sort by</SortLabel>
            <SelectWrapper>
              <SortSelect
                value={currentSort}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onSortChange(e.target.value as SortOption)
                }
              >
                <option value="LATEST">Latest</option>
                <option value="VIEWS">Views</option>
                <option value="BEST">Score</option>
              </SortSelect>
              <SelectIcon>
                <HiChevronDown size={14} />
              </SelectIcon>
            </SelectWrapper>
          </SortRow>
        )}

        <ClipList>
          {isLoading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <SkeletonRow key={i} style={{ marginBottom: i < 3 ? 2 : 0 }}>
                  <SkeletonCheckbox />
                  <SkeletonThumbnail />
                  <SkeletonInfo>
                    <SkeletonTitle />
                    <SkeletonMeta />
                  </SkeletonInfo>
                </SkeletonRow>
              ))}
            </>
          ) : clips.length === 0 ? (
            <EmptyText>No clips found</EmptyText>
          ) : (
            clips.map((clip) => (
              <ClipRow
                key={clip.id}
                onClick={() => onSelectClip(clip.id)}
                $selected={selectedClipIds.includes(clip.id)}
              >
                <Checkbox $checked={selectedClipIds.includes(clip.id)}>
                  {selectedClipIds.includes(clip.id) && <HiCheck size={12} />}
                </Checkbox>
                <ClipThumbnail src={clip.thumbnailUrl} alt={clip.title} />
                <ClipInfo>
                  <ClipTitle>{clip.title}</ClipTitle>
                  <ClipMeta>{formatNumber(clip.views)} views</ClipMeta>
                </ClipInfo>
                {onOpenClip && (
                  <OpenButton onClick={(e) => handleOpenClip(e, clip.id)}>
                    <HiExternalLink size={16} />
                  </OpenButton>
                )}
              </ClipRow>
            ))
          )}
        </ClipList>

        {selectedClipIds.length > 0 && (
          <PolicyNote mt="12px">
            <HiExclamationCircle size={14} style={{ flexShrink: 0 }} />
            {isUnlimited
              ? "Your account is exempt from clip expiration policies."
              : "Unsaved clips will convert to drafts and are subject to expiration."}
          </PolicyNote>
        )}
      </Box>

      <ModalFooter>
        <SecondaryButton onClick={onBack} disabled={isDeleting}>
          {backButtonText}
        </SecondaryButton>
        <ActionButton
          onClick={onDelete}
          disabled={selectedClipIds.length === 0 || isDeleting}
        >
          {isDeleting
            ? "Unsaving..."
            : `${actionLabel} Selected (${selectedClipIds.length})`}
        </ActionButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const SortRow = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const SortLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SortSelect = styled.select`
  appearance: none;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  padding: 6px 28px 6px 10px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.shade400};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  option {
    background: ${({ theme }) => theme.colors.navy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const SelectIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade400};
  display: flex;
  pointer-events: none;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const ClipList = styled(Box)`
  max-height: 320px;
  overflow-y: auto;
`;

const ClipRow = styled(Flex)<{ $selected: boolean }>`
  align-items: center;
  background: ${({ $selected }) =>
    $selected ? "rgba(255, 255, 255, 0.03)" : "transparent"};
  border-radius: 8px;
  cursor: pointer;
  gap: 12px;
  padding: 8px 10px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const Checkbox = styled(Box)<{ $checked: boolean }>`
  align-items: center;
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.alert : theme.colors.navy50};
  border: none;
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;
`;

const ClipThumbnail = styled.img`
  border-radius: 6px;
  flex-shrink: 0;
  height: 44px;
  object-fit: cover;
  width: 78px;
`;

const ClipInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClipMeta = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
`;

const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: 200px 0;
    }
  }
`;

const SkeletonBase = styled(Box)`
  ${shimmer}
  animation: shimmer 1.5s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.navy50} 0%,
    ${({ theme }) => theme.colors.navy} 50%,
    ${({ theme }) => theme.colors.navy50} 100%
  );
  background-size: 200px 100%;
  border-radius: 6px;
`;

const SkeletonRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
`;

const SkeletonCheckbox = styled(SkeletonBase)`
  border-radius: 4px;
  flex-shrink: 0;
  height: 18px;
  width: 18px;
`;

const SkeletonThumbnail = styled(SkeletonBase)`
  flex-shrink: 0;
  height: 40px;
  width: 72px;
`;

const SkeletonInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 14px;
  margin-bottom: 6px;
  width: 70%;
`;

const SkeletonMeta = styled(SkeletonBase)`
  height: 12px;
  width: 40%;
`;

const EmptyText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const OpenButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.shade400};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  transition: all 0.15s ease;
  width: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.alert};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PolicyNote = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.error};
  font-family: Barlow;
  font-size: 13px;
  gap: 6px;
  line-height: 1.4;
`;

const StorageSection = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.navy50};
`;

const StorageRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StorageCount = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
`;

const UpgradeLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
  padding: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ProgressBar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 5px;
  height: 8px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 5px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;
